import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/base/Breadcrumb';
import { profileIntro, profileFacts, profileExperience, profileAwards } from '@/mocks/profile';
import { usePageSeo } from '@/hooks/usePageSeo';
import PhotoCarousel from './components/PhotoCarousel';

export default function Profile() {
  usePageSeo({
    title: '小仓优子个人简介_出生资料与职业经历',
    description:
      '小仓优子个人简介，包含出生日期、出身地、职业经历、代表作品与获奖情况等详细资料，并附多张生活与活动照片，全面了解这位演员。',
    keywords: '小仓优子简介,小仓优子资料,演员个人资料,职业经历,获奖情况',
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full bg-gradient-to-br from-primary-50 via-background-50 to-secondary-50 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-8 md:py-12">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '演员简介' }]} />
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-1">
              <div className="w-full aspect-[4/5] rounded-lg overflow-hidden border border-background-200 bg-background-100">
                <img
                  src={profileIntro.avatar}
                  alt={`${profileIntro.name} 写真`}
                  title={`${profileIntro.name} 演员个人资料`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-5 flex items-center justify-between p-4 rounded-lg bg-secondary-100 border border-secondary-200">
                <div>
                  <div className="font-heading text-lg text-foreground-950">{profileIntro.name}</div>
                  <div className="text-xs tracking-[0.2em] text-secondary-800 mt-1">{profileIntro.nameEn}</div>
                </div>
                <Link
                  to="/category/works"
                  className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  看作品
                </Link>
              </div>
            </div>

            <div className="lg:col-span-2">
              <span className="text-xs tracking-[0.25em] text-accent-600">PROFILE</span>
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-3">小仓优子 · 个人简介</h1>
              <blockquote className="mt-5 pl-4 border-l-2 border-accent-400 text-base md:text-lg text-foreground-800 italic leading-relaxed">
                “{profileIntro.tagline}”
              </blockquote>
              <p className="text-sm md:text-base text-foreground-700 leading-loose mt-6">{profileIntro.bio}</p>
            </div>
          </div>

          <div className="mt-12 md:mt-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">个人资料</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background-200 rounded-lg overflow-hidden border border-background-200">
              {profileFacts.map((fact) => (
                <div key={fact.label} className="bg-background-50 px-5 py-4">
                  <div className="text-xs text-foreground-500">{fact.label}</div>
                  <div className="text-sm text-foreground-900 mt-1.5">{fact.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 md:mt-16 grid lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">职业经历</h2>
              <ol className="relative border-l border-background-300 ml-2 space-y-6">
                {profileExperience.map((exp) => (
                  <li key={exp.label} className="pl-6 relative">
                    <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-accent-500"></span>
                    <div className="font-heading text-base text-primary-600">{exp.label}</div>
                    <p className="text-sm text-foreground-700 leading-relaxed mt-1.5">{exp.value}</p>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">获奖情况</h2>
              <div className="flex flex-col gap-4">
                {profileAwards.map((award) => (
                  <div
                    key={`${award.year}-${award.title}`}
                    className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200"
                  >
                    <span className="w-12 h-12 flex-shrink-0 rounded-lg bg-secondary-200 text-secondary-900 flex items-center justify-center font-heading text-sm">
                      {award.year}
                    </span>
                    <div>
                      <div className="text-sm text-foreground-900 font-medium">{award.title}</div>
                      <div className="text-xs text-foreground-500 mt-1.5">{award.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12 md:mt-16">
            <div className="flex items-end justify-between mb-5">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">影像与生活照</h2>
              <span className="text-xs text-foreground-500">支持左右滑动 · 点击放大</span>
            </div>
            <PhotoCarousel />
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
            <Link
              to="/category/news"
              className="inline-flex items-center gap-1 px-6 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看最新动态
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}